import { HotFilterItem } from '@interfaces/api/responses/hot-filters.response';
import { getHotFilterItemById } from './hot-filters';

// Update the parent checked state based on the checked state of children
export const updateParentCheckedState = (children: HotFilterItem[], parentID: string): HotFilterItem[] => {
  return children.map((child) => {
    if (child.id === parentID) {
      return {
        ...child,
        isChecked: child.children.some((c) => c.isChecked),
      };
    }
    if (child.children && child.children.length > 0) {
      return { ...child, children: updateParentCheckedState(child.children, parentID) };
    }
    return child;
  });
};

// Update the checked state for a specific item in the list
export const updateCheckedState = (
  children: HotFilterItem[],
  childItem: HotFilterItem,
  isChecked: boolean,
): HotFilterItem[] => {
  return children.map((child) => {
    if (child.id === childItem.id) {
      return { ...child, isChecked };
    }
    if (child.children && child.children.length > 0) {
      return { ...child, children: updateCheckedState(child.children, childItem, isChecked) };
    }
    return child;
  });
};

// Handle the "select all" logic
const handleSelectAll = (children: HotFilterItem[], parentID: string, isChecked: boolean) => {
  const parent = getHotFilterItemById(children, parentID);
  if (!parent) {
    return children;
  }

  const updatedParent = {
    ...parent,
    isChecked,
    children: parent.children.map((child) => ({ ...child, isChecked })),
  };

  return children.map((child) => (child.id === parent.id ? updatedParent : child));
};

// Update the "all" item state based on the checked state of siblings
const updateAllItemIfNeeded = (children: HotFilterItem[], parentID: string) => {
  const parent = getHotFilterItemById(children, parentID);
  if (!parent) {
    return children;
  }

  const allItem = parent.children.find((child) => child.id === `all_${parent.id}`);
  if (!allItem) {
    return children;
  }

  const allChecked = parent.children.every((sibling) => sibling.isChecked || sibling.id === `all_${parent.id}`);

  const updatedAllItem = { ...allItem, isChecked: allChecked };

  const updatedParent = {
    ...parent,
    children: parent.children.map((child) => (child.id === allItem.id ? updatedAllItem : child)),
  };

  return children.map((child) => (child.id === parent.id ? updatedParent : child));
};

export const updateSelectedState = (children: HotFilterItem[], childItem: HotFilterItem) => {
  // Update the checked state for the child item
  const isChecked = !childItem.isChecked;
  const updatedChildren = updateCheckedState(children, childItem, isChecked);

  // Handle "select all" case
  if (childItem.id.startsWith('all_')) {
    return handleSelectAll(updatedChildren, childItem.parentID, isChecked);
  } else {
    // If it's not "select all", update the parent checked state
    const updatedWithParentChecked = updateParentCheckedState(updatedChildren, childItem.parentID);

    return updateAllItemIfNeeded(updatedWithParentChecked, childItem.parentID);
  }
};
